import React, { useCallback } from "react";

import type { SkeletonProps, StackProps, TypographyVariant } from "@mui/material";
import { Skeleton, Stack } from "@mui/material";
import type { TypographyPropsVariantOverrides } from "@mui/material/Typography";

import { BLUE } from "theme/colors";
import { Definitions as TypographyDefinitions } from "theme/typography";

function getTypographyHeight(variant?: TypographyVariant | keyof TypographyPropsVariantOverrides): string {
  if (!variant || !(variant in TypographyDefinitions)) {
    return "14px";
  }
  const fontSize = TypographyDefinitions[variant as keyof typeof TypographyDefinitions]?.fontSize;
  return fontSize ? `${fontSize}px` : "14px";
}

export interface SkeletonContainerElement extends Omit<StackProps, "children"> {
  type: "column" | "row";
  children?: SkeletonElement[];
}

interface SkeletonBaseElement extends SkeletonProps {
  type: "column" | "row" | "skeleton";
}

interface SkeletonTextElement extends SkeletonBaseElement {
  typographyVariant?: TypographyVariant | keyof TypographyPropsVariantOverrides;
  lines?: number;
}

type SkeletonElement = SkeletonBaseElement | SkeletonContainerElement | SkeletonTextElement;

interface SkeletonLayoutProps {
  layout: SkeletonElement | SkeletonElement[];
}

export const SkeletonLayout = React.memo<SkeletonLayoutProps>(({ layout }) => {
  const renderElement = useCallback((element: SkeletonElement, index: number): React.ReactNode => {
    const elementKey = `${element.type}-${index}`;

    switch (element.type) {
      case "column":
      case "row": {
        const { children, type, height = "auto", width = "100%", ...rest } = element as SkeletonContainerElement;

        return (
          <Stack key={elementKey} direction={type} boxSizing="border-box" height={height} width={width} {...rest}>
            {children?.map((child, i) => renderElement(child, i))}
          </Stack>
        );
      }
      default: {
        const animation = element.animation || "wave";
        const skeletonSx = {
          backgroundColor: BLUE[10],
          "&::after": {
            background: `linear-gradient(90deg, transparent, ${BLUE[20]}, transparent)`,
          },
        };

        switch (element.variant) {
          case "circular":
          case "rectangular":
          case "rounded": {
            const { variant, height = "40px", width = "40px", sx: customSx, ...rest } = element as SkeletonBaseElement;

            return (
              <Skeleton
                key={elementKey}
                variant={variant}
                height={height}
                width={width}
                animation={animation}
                sx={{
                  flexShrink: 0,
                  ...skeletonSx,
                  ...customSx,
                }}
                {...rest}
              />
            );
          }
          case "text": {
            const {
              typographyVariant,
              lines = 1,
              height = getTypographyHeight(typographyVariant),
              width = "100%",
              sx: customSx,
              ...rest
            } = element as SkeletonTextElement;

            if (lines === 1) {
              return (
                <Skeleton
                  key={elementKey}
                  variant="text"
                  height={height}
                  width={width}
                  animation={animation}
                  sx={{
                    transform: "none",
                    transformOrigin: "unset",
                    ...skeletonSx,
                    ...customSx,
                  }}
                  {...rest}
                />
              );
            }

            return (
              <Stack key={elementKey} spacing={1}>
                {Array.from({ length: lines }).map((_, i) => (
                  <Skeleton
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${elementKey}-line-${i}`}
                    variant="text"
                    height={height}
                    width={i === lines - 1 ? "70%" : width}
                    animation={animation}
                    sx={{
                      transform: "none",
                      transformOrigin: "unset",
                      ...skeletonSx,
                      ...customSx,
                    }}
                    {...rest}
                  />
                ))}
              </Stack>
            );
          }
          default: {
            return null;
          }
        }
      }
    }
  }, []);

  return <>{Array.isArray(layout) ? layout.map(renderElement) : renderElement(layout, 0)}</>;
});

SkeletonLayout.displayName = "SkeletonLayout";
